import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api.config';
import AIBackgroundEffects from './AIBackgroundEffects';
import NotificationToast from './NotificationToast';
import './ClientDashboard.css';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [agentStatusPolling, setAgentStatusPolling] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    // Verificar autenticación
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);

    // Cargar suscripción del usuario (el backend devuelve 'id', no '_id')
    const userId = parsedUser.id || parsedUser._id;
    fetchUserSubscription(token, userId);
  }, [navigate]);

  const fetchUserSubscription = async (token, userId) => {
    try {
      const response = await axios.get(
        buildApiUrl(`/api/subscriptions/user/${userId}`),
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success && response.data.data) {
        setSubscription(response.data.data);

        // Si el agente está en proceso de creación, iniciar polling
        if (response.data.data.agentStatus === 'creating') {
          startAgentStatusPolling(token, response.data.data._id);
        }
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      if (error.response?.status !== 404) {
        setNotification({
          message: error.response?.data?.message || 'Error al cargar suscripción',
          type: 'error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const startAgentStatusPolling = (token, subscriptionId) => {
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(
          buildApiUrl(`/api/subscriptions/${subscriptionId}`),
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.success) {
          setSubscription(response.data.data);

          // Detener polling si el agente ya está listo o hubo error
          if (response.data.data.agentStatus !== 'creating') {
            clearInterval(interval);
            setAgentStatusPolling(null);
          }
        }
      } catch (error) {
        console.error('Error polling agent status:', error);
      }
    }, 5000); // Cada 5 segundos

    setAgentStatusPolling(interval);
  };

  useEffect(() => {
    // Limpiar polling al desmontar
    return () => {
      if (agentStatusPolling) {
        clearInterval(agentStatusPolling);
      }
    };
  }, [agentStatusPolling]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setNotification({
      message: 'Copiado al portapapeles',
      type: 'success'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      creating: { text: 'Creando', icon: '⏳', color: '#f59e0b' },
      ready: { text: 'Listo', icon: '✓', color: '#10b981' },
      error: { text: 'Error', icon: '✕', color: '#ef4444' },
      failed: { text: 'Fallido', icon: '✕', color: '#ef4444' }
    };

    const badge = badges[status] || badges.creating;

    return (
      <span
        className="client-dashboard-badge"
        style={{ background: `${badge.color}20`, color: badge.color, borderColor: `${badge.color}50` }}
      >
        <span className="client-dashboard-badge-icon">{badge.icon}</span>
        {badge.text}
      </span>
    );
  };

  const getSubscriptionBadge = (status) => {
    const badges = {
      active: { text: 'Activa', icon: '✓', color: '#10b981' },
      pending: { text: 'Pendiente', icon: '⏳', color: '#f59e0b' },
      cancelled: { text: 'Cancelada', icon: '✕', color: '#ef4444' }
    };

    const badge = badges[status] || badges.pending;

    return (
      <span
        className="client-dashboard-badge"
        style={{ background: `${badge.color}20`, color: badge.color, borderColor: `${badge.color}50` }}
      >
        <span className="client-dashboard-badge-icon">{badge.icon}</span>
        {badge.text}
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-GT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="client-dashboard-loading">
        <AIBackgroundEffects step={1} />
        <div className="client-dashboard-spinner"></div>
        <p>Cargando tu panel...</p>
      </div>
    );
  }

  return (
    <div className="client-dashboard-container">
      <AIBackgroundEffects step={1} />

      {/* Header */}
      <div className="client-dashboard-header">
        <div className="client-dashboard-header-content">
          <div className="client-dashboard-brand">
            <h1 className="client-dashboard-title">🤖 AI Voice Agent</h1>
            <p className="client-dashboard-subtitle">Panel de Control del Cliente</p>
          </div>
          <button onClick={handleLogout} className="client-dashboard-logout-btn">
            Cerrar Sesión
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="client-dashboard-main">
        {/* User Welcome Card */}
        <div className="client-dashboard-welcome-card">
          <h2>Bienvenido, {user?.contactName || user?.username}</h2>
          <p>Gestiona tu agente de voz IA y suscripción</p>
        </div>

        {/* Agents Table */}
        {subscription ? (
          <div className="client-dashboard-table-container">
            <div className="client-dashboard-table-header">
              <h3>Mis Agentes de Voz</h3>
            </div>

            <div className="client-dashboard-table-wrapper">
              <table className="client-dashboard-table">
                <thead>
                  <tr>
                    <th>Negocio</th>
                    <th>Sitio Web</th>
                    <th>Estado del Agente</th>
                    <th>Suscripción</th>
                    <th>Monto Mensual</th>
                    <th>Fecha de Registro</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <div className="client-dashboard-business-cell">
                        <span className="client-dashboard-business-name">{subscription.businessName}</span>
                        <span className="client-dashboard-contact-name">{subscription.contactName}</span>
                      </div>
                    </td>
                    <td>
                      <a
                        href={subscription.businessWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="client-dashboard-website-link"
                      >
                        {subscription.businessWebsite}
                      </a>
                    </td>
                    <td>{getStatusBadge(subscription.agentStatus)}</td>
                    <td>{getSubscriptionBadge(subscription.subscriptionStatus)}</td>
                    <td>
                      <span className="client-dashboard-amount">${subscription.subscriptionAmount}</span>
                    </td>
                    <td>{formatDate(subscription.registrationDate)}</td>
                    <td>
                      <div className="client-dashboard-actions-cell">
                        <button
                          onClick={() => setShowDetailsModal(true)}
                          className="client-dashboard-details-btn"
                        >
                          📋 Ver Detalles
                        </button>
                        {subscription.agentStatus === 'ready' && subscription.elevenLabsAgent?.agentId && (
                          <a
                            href={subscription.agentTestUrl || `https://elevenlabs.io/app/conversational-ai?agent_id=${subscription.elevenLabsAgent.agentId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="client-dashboard-test-btn-small"
                          >
                            🎤 Probar
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="client-dashboard-no-subscription">
            <div className="client-dashboard-empty-icon">📭</div>
            <h3>No tienes ninguna suscripción activa</h3>
            <p>Contacta al administrador para activar tu servicio de agente de voz</p>
          </div>
        )}

        {/* Footer */}
        <div className="client-dashboard-footer">
          <p>© 2025 AI Voice Agent - Desarrollado por Rafael Martinez</p>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && subscription && (
        <div className="client-dashboard-modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="client-dashboard-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="client-dashboard-modal-header">
              <h2>🤖 Detalles Completos del Agente</h2>
              <button
                className="client-dashboard-modal-close"
                onClick={() => setShowDetailsModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="client-dashboard-modal-body">
              {/* Información del Negocio */}
              <div className="client-dashboard-detail-section">
                <h3>📊 Información del Negocio</h3>
                <div className="client-dashboard-detail-grid">
                  <div className="client-dashboard-detail-item">
                    <label>Nombre del Negocio</label>
                    <p>{subscription.businessName}</p>
                  </div>
                  <div className="client-dashboard-detail-item">
                    <label>Sitio Web</label>
                    <p>
                      <a href={subscription.businessWebsite} target="_blank" rel="noopener noreferrer">
                        {subscription.businessWebsite}
                      </a>
                    </p>
                  </div>
                  <div className="client-dashboard-detail-item">
                    <label>Nombre de Contacto</label>
                    <p>{subscription.contactName}</p>
                  </div>
                  <div className="client-dashboard-detail-item">
                    <label>Email</label>
                    <p>{subscription.email}</p>
                  </div>
                  <div className="client-dashboard-detail-item">
                    <label>Teléfono</label>
                    <p>{subscription.phoneNumber}</p>
                  </div>
                  <div className="client-dashboard-detail-item">
                    <label>Fecha de Registro</label>
                    <p>{formatDate(subscription.registrationDate)}</p>
                  </div>
                </div>
              </div>

              {/* Estado del Agente */}
              <div className="client-dashboard-detail-section">
                <h3>🎯 Estado del Agente</h3>
                <div className="client-dashboard-detail-grid">
                  <div className="client-dashboard-detail-item">
                    <label>Estado Actual</label>
                    <p>{getStatusBadge(subscription.agentStatus)}</p>
                  </div>
                  {subscription.elevenLabsAgent?.agentId && (
                    <>
                      <div className="client-dashboard-detail-item">
                        <label>ID del Agente</label>
                        <p className="client-dashboard-code">{subscription.elevenLabsAgent.agentId}</p>
                      </div>
                      <div className="client-dashboard-detail-item">
                        <label>Nombre del Agente</label>
                        <p>{subscription.elevenLabsAgent.agentName || `Agente de Voz ${subscription.businessName}`}</p>
                      </div>
                      <div className="client-dashboard-detail-item">
                        <label>Fecha de Creación</label>
                        <p>{formatDate(subscription.elevenLabsAgent.createdAt)}</p>
                      </div>
                    </>
                  )}
                  {subscription.elevenLabsAgent?.errorDetails && (
                    <div className="client-dashboard-detail-item client-dashboard-full-width">
                      <label>Detalles del Error</label>
                      <p className="client-dashboard-error-text">{subscription.elevenLabsAgent.errorDetails}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* URL de Prueba */}
              {subscription.agentStatus === 'ready' && subscription.elevenLabsAgent?.agentId && (
                <div className="client-dashboard-detail-section">
                  <h3>🔗 Acceso al Agente</h3>
                  <div className="client-dashboard-url-container">
                    <label>URL de Prueba</label>
                    <div className="client-dashboard-url-box">
                      <input
                        type="text"
                        value={subscription.agentTestUrl || `https://elevenlabs.io/app/conversational-ai?agent_id=${subscription.elevenLabsAgent.agentId}`}
                        readOnly
                      />
                      <button
                        onClick={() => copyToClipboard(subscription.agentTestUrl || `https://elevenlabs.io/app/conversational-ai?agent_id=${subscription.elevenLabsAgent.agentId}`)}
                        className="client-dashboard-copy-btn"
                      >
                        📋 Copiar
                      </button>
                    </div>
                    <a
                      href={subscription.agentTestUrl || `https://elevenlabs.io/app/conversational-ai?agent_id=${subscription.elevenLabsAgent.agentId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="client-dashboard-test-btn"
                    >
                      🎤 Probar Agente en ElevenLabs
                    </a>
                  </div>
                </div>
              )}

              {/* Configuración del Bot */}
              {subscription.botCustomization && (
                <div className="client-dashboard-detail-section">
                  <h3>⚙️ Configuración del Bot</h3>
                  <div className="client-dashboard-detail-grid">
                    <div className="client-dashboard-detail-item">
                      <label>Tipo de Voz</label>
                      <p>{subscription.botCustomization.voiceType === 'female' ? '👩 Femenina' : '👨 Masculina'}</p>
                    </div>
                    <div className="client-dashboard-detail-item client-dashboard-full-width">
                      <label>Primer Mensaje</label>
                      <p className="client-dashboard-message-text">{subscription.botCustomization.firstMessage}</p>
                    </div>
                    {subscription.botCustomization.systemPrompt && (
                      <div className="client-dashboard-detail-item client-dashboard-full-width">
                        <label>Prompt del Sistema</label>
                        <pre className="client-dashboard-prompt-text">{subscription.botCustomization.systemPrompt}</pre>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Base de Conocimiento */}
              {subscription.elevenLabsAgent?.knowledgeBaseUrls && subscription.elevenLabsAgent.knowledgeBaseUrls.length > 0 && (
                <div className="client-dashboard-detail-section">
                  <h3>📚 Base de Conocimiento</h3>
                  <div className="client-dashboard-kb-list">
                    {subscription.elevenLabsAgent.knowledgeBaseUrls.map((url, index) => (
                      <div key={index} className="client-dashboard-kb-item">
                        <span className="client-dashboard-kb-icon">🔗</span>
                        <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Archivos Cargados */}
              {subscription.uploadedFiles && subscription.uploadedFiles.length > 0 && (
                <div className="client-dashboard-detail-section">
                  <h3>📁 Archivos Cargados</h3>
                  <div className="client-dashboard-files-list">
                    {subscription.uploadedFiles.map((file, index) => (
                      <div key={index} className="client-dashboard-file-item">
                        <div className="client-dashboard-file-info">
                          <span className="client-dashboard-file-icon">📄</span>
                          <div>
                            <p className="client-dashboard-file-name">{file.originalName}</p>
                            <p className="client-dashboard-file-meta">
                              {(file.fileSize / 1024).toFixed(2)} KB • {formatDate(file.uploadDate)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notas Adicionales */}
              {subscription.additionalNotes && (
                <div className="client-dashboard-detail-section">
                  <h3>📝 Notas Adicionales</h3>
                  <p className="client-dashboard-notes-text">{subscription.additionalNotes}</p>
                </div>
              )}

              {/* Información de Suscripción */}
              <div className="client-dashboard-detail-section">
                <h3>💳 Información de Suscripción</h3>
                <div className="client-dashboard-detail-grid">
                  <div className="client-dashboard-detail-item">
                    <label>Estado de Suscripción</label>
                    <p>{getSubscriptionBadge(subscription.subscriptionStatus)}</p>
                  </div>
                  <div className="client-dashboard-detail-item">
                    <label>Monto Mensual</label>
                    <p className="client-dashboard-price">${subscription.subscriptionAmount}</p>
                  </div>
                  {subscription.activationDate && (
                    <div className="client-dashboard-detail-item">
                      <label>Fecha de Activación</label>
                      <p>{formatDate(subscription.activationDate)}</p>
                    </div>
                  )}
                  {subscription.nextPaymentDate && (
                    <div className="client-dashboard-detail-item">
                      <label>Próximo Pago</label>
                      <p>{formatDate(subscription.nextPaymentDate)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="client-dashboard-modal-footer">
              <button
                className="client-dashboard-modal-close-btn"
                onClick={() => setShowDetailsModal(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default ClientDashboard;
